import { ValidateEmail } from "@swivl-mono/helpers";
import { Button, ButtonVariant, Input, PhoneInput, Tooltip } from "@swivl-mono/ui";
import parsePhoneNumberFromString from "libphonenumber-js";
import { useState } from "react";
import { FiMinus } from "react-icons/fi";
import { IoInformationCircleOutline } from "react-icons/io5";

// Empty is valid; a non-empty value must parse correctly.
export function validatePhone(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = parsePhoneNumberFromString(trimmed);
  return parsed && parsed.isValid() ? null : "Enter a valid phone number.";
}

// Same empty-is-valid contract as validatePhone.
export function validateEmail(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return ValidateEmail(trimmed) ? null : "Enter a valid email address.";
}

export function FlyoutField(props: { label: string; tooltip?: string; required?: boolean; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <span className="text-sm font-semibold text-grey-600">
          {props.label}
          {props.required && <span className="text-red-500"> *</span>}
        </span>
        {props.tooltip && (
          <Tooltip content={props.tooltip}>
            <IoInformationCircleOutline className="size-4 text-grey-500" />
          </Tooltip>
        )}
      </div>
      {props.children}
    </div>
  );
}

export function RecipientListEditor(props: {
  label: string;
  tooltip?: string;
  entries: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  inputType?: "text" | "phone";
  validate?: (value: string) => string | null;
}): React.ReactElement {
  const { label, tooltip, entries, onChange, placeholder, inputType = "text", validate } = props;

  // Always show one empty row; blanks are filtered before saving.
  const displayEntries = entries.length > 0 ? entries : [""];

  // Bumped on remove to force phone rows to re-seed correctly.
  const [removeVersion, setRemoveVersion] = useState(0);

  // Per-row errors, keyed by index; set on blur.
  const [errors, setErrors] = useState<Record<number, string | null>>({});

  const setAt = (idx: number, value: string): void => {
    const next = displayEntries.slice();
    next[idx] = value;
    onChange(next);
    if (errors[idx]) setErrors((e) => ({ ...e, [idx]: null }));
  };
  const removeAt = (idx: number): void => {
    const next = displayEntries.slice();
    next.splice(idx, 1);
    onChange(next);
    setRemoveVersion((v) => v + 1);
    // Indices shift after removal, so clear errors here.
    setErrors({});
  };
  const validateAt = (idx: number, value: string): void => {
    if (validate) setErrors((e) => ({ ...e, [idx]: validate(value) }));
  };

  return (
    <FlyoutField label={label} tooltip={tooltip}>
      <div className="flex flex-col gap-2">
        {displayEntries.map((entry, idx) => (
          <div key={inputType === "phone" ? `phone-${removeVersion}-${idx}` : idx} className="flex items-center gap-3">
            <div className="flex-1">
              {inputType === "phone" ? (
                <PhoneRecipientField
                  initial={entry}
                  placeholder={placeholder}
                  onChange={(v) => setAt(idx, v)}
                  onBlur={validate ? () => validateAt(idx, displayEntries[idx]) : undefined}
                  error={errors[idx] ?? undefined}
                />
              ) : (
                <Input
                  value={entry}
                  placeholder={placeholder}
                  onChange={(e) => setAt(idx, e.target.value)}
                  onBlur={validate ? () => validateAt(idx, displayEntries[idx]) : undefined}
                  error={errors[idx] ?? undefined}
                />
              )}
            </div>
            <button
              type="button"
              aria-label="Remove recipient"
              onClick={() => removeAt(idx)}
              className="cursor-pointer text-grey-900 hover:text-red-600"
            >
              <FiMinus className="size-4" />
            </button>
          </div>
        ))}
        <div>
          <Button
            variant={ButtonVariant.GHOST}
            className="rounded-sm border border-grey-200 hover:border-orange-500"
            onClick={() => onChange([...displayEntries, ""])}
          >
            Add Recipient
          </Button>
        </div>
      </div>
    </FlyoutField>
  );
}

// Freezes initial value; PhoneInput would re-parse mid-typing otherwise.
function PhoneRecipientField(props: {
  initial: string;
  placeholder: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
}): React.ReactElement {
  const [seed] = useState(props.initial);
  return <PhoneInput defaultValue={seed} placeholder={props.placeholder} onChange={props.onChange} onBlur={props.onBlur} error={props.error} />;
}
